import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import api from 'helpers/api'
import classNames from 'classnames'
import Collapsible from 'components/common/Collapsible/Collapsible'
import Button from 'components/common/Button/Button'
import Link from 'components/common/Link/Link'
import LogoShort from 'components/assets/icons/LogoShort'
import CreditCard from 'components/assets/icons/CreditCard'
import CashOut from 'components/assets/icons/CashOut'
import Title from 'components/common/PageTitle/PageTitle'
import { next } from 'components/Freemium/Nav/Nav'
import { Capitalize, dateToAge } from 'functions'
import Checkbox from 'components/common/Checkbox/Checkbox'
import style from './Summary.module.scss'
import mainStyle from '../FreemiumComponents.module.scss'

const Summary = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { state, pathname } = useLocation()

  const contracts = state?.contract?.contractType?.map( contract => contract?.name.replace('Offre ', '') ) || []
  const [isFreeAccepted, setIsFreeAccepted] = useState(!contracts.includes('Zendays'))
  const [isMiumAccepted, setIsMiumAccepted] = useState(!contracts.includes('Serenity'))
  const [isIbanPayment, setIbanPayment] = useState(false)
  const [price, setPrice] = useState(null)

  useEffect(() => {
    const fetchPrice = async () => {
      let price = Number(state.contract.calculatedPrice.replace(',','.'))
      for (const child of state.client.children || []) {
        try {
          const age = dateToAge(child.birthdate)
          const request = await api.get({ url: `/freemium/ageChild/${age}` })
          price = parseFloat(price + Number(request.result || 0)).toFixed(2)
        } catch (err) {
          console.error(`Failed to fetch price for ${child.firstname}:`, err.message)
        }
      }
      setPrice(price)
    }
    fetchPrice()
  }, [state])

  const onSubmit = () => {
    next({
      pathname, navigate, state,
      data: {
        isIbanPayment,
        transactionHorodate: new Date(),
        contract: { paymentPlan: 'month' },
        listingData: { areTermsAccepted: isFreeAccepted && isMiumAccepted }
      },
      skip: !state.client.paidOption && 1
    })
  }


  if (!state) return null

  const { contractType } = state.contract
  const {
    firstname,
    lastname,
    birthdate,
    address,
    zipcode,
    city,
    additionalAddress,
    phone: { number },
    email,
    partnerOption,
    firstnamePartner,
    lastnamePartner,
    birthdatePartner,
    paidOption
  } = state.client

  const texts = contractType.map( contract => contract.wordings?.summary )
  const {
    titlePromotion,
    legalTerms,
    edit
  } = contractType[0].wordings?.summary || {}

  const date = new Date()
  date.setMonth( date.getMonth() + 3 )
  const day = ('0'+date.getDate()).slice(-2)
  const month = ('0'+Number(date.getMonth() + 1)).slice(-2)
  const year = date.getFullYear()
  const paymentStart =  `${day}/${month}/${year}`

  const dateFormat = (date) => `01/${('0'+Number(date.getMonth() + 1)).slice(-2)}/${date.getFullYear()}`
  const startDate = (isFree) => {
    const today = new Date()
    // Free
    if (isFree){
      return new Date(
        today.getFullYear(),
        today.getMonth() + 1,
        1
      )
    }
    // Mium + free activated
    if (state.endFree) {
      const endFree = new Date(state.endFree)
      const selectedDate = endFree > new Date()
        ? endFree
        : today
      return new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate() + 1
      )
    }
    // Mium + free not activated yet
    return new Date(
      today.getFullYear(),
      today.getMonth() + 4,
      1
    )
  }

  return (
    <>
      <Title className={mainStyle['page-title']} text={<>{t('freemium:summary.title')} 👌</>} />

      <div className={style.card}>

        <div className={style['card-top']}>
          <div className={style['price-month']}>
            <span>{titlePromotion || 'titlePromotion'}</span>
          </div>

          {paidOption && <span className={style['price-year']}>
            {t('freemium:summary.then', { price })}
          </span>}
        </div>


        {texts.map( (text, idx) =>{
          const free = text.titleGuarantees?.includes('3 mois')
          const coveredEvents = ((free
            ? contractType[0].wordings.home
            : contractType.slice(-1)[0].wordings.more
          )?.coveredEvents || '').split('//')

          return (<div className={style.offer} key={`offer-${idx}`}>
            <h4 className={classNames(style['summary-title'], style.address)}>
              <span>
                {free
                  ? "🎁"
                  : <LogoShort className={style.logo} />
                }
                &nbsp;{text.titleGuarantees}
                <br/><span className={style['summary-start-date']}>{t('freemium:summary.startDate', { date: dateFormat(startDate(free)) })}</span>
              </span>
            </h4>
            <Collapsible
              className={style.collapsible}
              title={<span className={style['list-title']}>{text.subtitleGuarantees}</span>}
              price={free
                ? text.free
                : t('freemium:summary.price', { price })
              }
              content={
                <ul className={style.list}>
                  {coveredEvents.map((item, index) => (
                    <li key={`covered-events-${index}-${idx}`}>
                      {item}
                    </li>
                  ))}
                </ul>
              }
            />
          </div>)
        })}
      </div>


      <div className={style.card}>
        <div className={style['card-content']}>
          <div className={style.summary}>

            <div className={style['summary-row']}>
              <h4 className={classNames(style['summary-title'], style.address)}>
                👤 {t('freemium:summary.yourInfos')}
                <Link className={style['address-link']} label={edit || 'edit'} state={state} to="../profil" />
              </h4>
              <p className={style['summary-description']}>
                {`${Capitalize(firstname)} ${Capitalize(lastname)}, ${t('common:form.born')} ${birthdate}`}
              </p>
            </div>

            <div className={style['summary-row']}>
              <h4 className={classNames(style['summary-title'], style.address)}>
                🏠 {t('freemium:summary.yourAddress')}
                <Link className={style['address-link']} label={edit || 'edit'} state={state} to="../address" />
              </h4>
              <p className={style['summary-description']}>
                {`${address}, ${zipcode} ${city}`}
                {additionalAddress && `, ${additionalAddress}`}
              </p>
            </div>

            <div className={style['summary-row']}>
              <h4 className={classNames(style['summary-title'], style.address)}>
                💌 {t('freemium:summary.yourContacts')}
                <Link className={style['address-link']} label={edit || 'edit'} state={state} to="../contacts" />
              </h4>
              <p className={style['summary-description']}>
                {`${email}, +${number}`}
              </p>
            </div>

            {partnerOption && <div className={style['summary-row']}>
              <h4 className={classNames(style['summary-title'], style.address)}>
                👶 {t('freemium:summary.yourPartner')}
                <Link className={style['address-link']} label={edit || 'edit'} state={state} to="../partner" />
              </h4>
              <p className={style['summary-description']}>
                {`${Capitalize(firstnamePartner)} ${Capitalize(lastnamePartner)}, ${t('common:form.born')} ${birthdatePartner}`}
              </p>
            </div>}

            {!!state.client.children?.length && <div className={style['summary-row']}>
              <h4 className={classNames(style['summary-title'], style.address)}>
                👶 {texts.slice(-1)[0].childrenTitle || 'childrenTitle'}
                <Link className={style['address-link']} label={edit || 'edit'} state={state} to="../children" />
              </h4>
              {state.client.children.map( (child, idx) => (
                <p className={style['summary-description']} key={`child-${idx}`}>
                  {`${Capitalize(child.firstname)} ${Capitalize(child.lastname)}, ${t('common:form.born')} ${child.birthdate}`}
                </p>
              ))}
            </div>}

          </div>
        </div>
      </div>


      {paidOption && <div className={style.payment}>
        <h3 className={style['payment-title']}>{t('freemium:summary.paymentMethod.title')}</h3>
        <span className={style['payment-start']}>{t('freemium:summary.paymentStartAt', {paymentStart})}</span>
        <div className={style['payment-methods']}>
          <div
            className={classNames(style['payment-method'], !isIbanPayment && style.selected)}
            onClick={() => setIbanPayment(false)}
          >
            <CreditCard styles={{ fill: !isIbanPayment ? '#fff' : '#8686FF' }} />
            <p>{t('freemium:summary.paymentMethod.creditCard')}</p>
          </div>

          <div
            className={classNames(style['payment-method'], isIbanPayment && style.selected)}
            onClick={() => setIbanPayment(true)}
          >
            <CashOut styles={{ fill: isIbanPayment ? '#fff' : '#8686FF' }} />
            <p>{t('freemium:summary.paymentMethod.iban')}</p>
          </div>
        </div>
      </div>}

      {state?.contract?.contractType?.map( contract => {
        const name = contract?.name.replace('Offre ', '')
        return (
          <Checkbox
            id="terms"
            className={style.checkbox}
            key={`checkbox-${name}`}
            label={
              <p className={style['checkbox-label']}>
                {t('freemium:summary.acceptSubscribing', {name})}
                &nbsp;
                <Link
                  className={style.link}
                  label={t('freemium:summary.ni')}
                  target="_blank"
                  to={contract.legalDocuments.ni}
                />
                &nbsp;
                {t('freemium:summary.acceptSubscribingAnd')}
                &nbsp;
                <Link
                  className={style.link}
                  label={t('freemium:summary.ipid')}
                  target="_blank"
                  to={contract.legalDocuments.ipid}
                />
              </p>
            }
            onChange={() => name === 'Zendays' ? setIsFreeAccepted(!isFreeAccepted) : setIsMiumAccepted(!isMiumAccepted)}
          />
        )
      })}

      <p className={style['legal-terms']}>{legalTerms || 'legalTerms'}</p>

      <Button
        className={mainStyle.button}
        label={t('common:join')}
        disabled={!isFreeAccepted || !isMiumAccepted}
        onClick={onSubmit}
        type="primary"
      />
    </>
  )
}

export default Summary
